import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function SessionTimeout({ children }) {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [timeoutMinutes, setTimeoutMinutes] = useState(30);
  const [warningMinutes, setWarningMinutes] = useState(1);
  const [isEnabled, setIsEnabled] = useState(true);
  
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await fetch("/api/Settings/session-timeout", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeoutMinutes(data.timeoutMinutes || 30);
        setWarningMinutes(data.warningMinutes || 1);
        setIsEnabled(data.isEnabled !== false); // Default to true
      }
    } catch (err) {
      console.log("Using default session timeout settings");
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    navigate("/login", { state: { message: "Session expired. Please login again." } });
  }, [navigate]);

  const clearAllTimers = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const resetTimers = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || !isEnabled) return;

    lastActivityRef.current = Date.now();
    setShowWarning(false);
    clearAllTimers();

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(warningMinutes * 60);
      
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningMs);

    timeoutRef.current = setTimeout(() => {
      logout();
    }, timeoutMs);
  }, [timeoutMinutes, warningMinutes, isEnabled, logout]);

  const extendSession = () => {
    setShowWarning(false);
    resetTimers();
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !isEnabled) return;

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > 1000) {
        lastActivityRef.current = now;
        if (!showWarning) {
          resetTimers();
        }
      }
    };

    events.forEach(event => window.addEventListener(event, handleActivity));
    resetTimers();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearAllTimers();
    };
  }, [resetTimers, showWarning, isEnabled]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      {children}
      
      {showWarning && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",backgroundColor:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}}>
          <div style={{backgroundColor:"white",padding:"40px",borderRadius:"12px",textAlign:"center",maxWidth:"400px",width:"90%",boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
            <div style={{fontSize:"48px",marginBottom:"15px"}}>⏰</div>
            <h2 style={{margin:"0 0 15px 0",color:"#333"}}>Session Expiring Soon</h2>
            <p style={{color:"#666",fontSize:"16px",margin:"0 0 10px 0"}}>
              Your session will expire in <strong style={{color:"#dc3545",fontSize:"24px"}}>{formatTime(countdown)}</strong>
            </p>
            <p style={{color:"#888",fontSize:"14px",margin:"0 0 25px 0"}}>
              Click below to continue or you'll be logged out.
            </p>
            <div style={{display:"flex",gap:"15px",justifyContent:"center"}}>
              <button onClick={extendSession} style={{padding:"12px 30px",backgroundColor:"#28a745",color:"white",border:"none",borderRadius:"6px",fontSize:"16px",fontWeight:"600",cursor:"pointer"}}>
                ✅ Stay Logged In
              </button>
              <button onClick={logout} style={{padding:"12px 30px",backgroundColor:"white",color:"#dc3545",border:"2px solid #dc3545",borderRadius:"6px",fontSize:"16px",fontWeight:"600",cursor:"pointer"}}>
                🚪 Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

